"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const experienceSteps = [{
  title: "Family-Centered Consultation",
  description: "Your journey begins with a deep clinical assessment where we listen to your story. We treat every patient with the same compassion and respect as our own family, ensuring you feel heard and understood from day one.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/mindsage_arrival_lobby.png"
}, {
  title: "Minimally Invasive Precision",
  description: "Experience the future of neurosurgery. Our multidisciplinary team utilizes state-of-the-art technology to perform precise procedures through smaller incisions, resulting in less pain and significantly shorter recovery times.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/mindsage_session_chair.png"
}, {
  title: "Compassionate Recovery",
  description: "Our care extends far beyond the procedure. We provide a structured, personalized path to recovery that integrates both surgical and non-surgical techniques to help you return to your life with renewed clarity and strength.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/mindsage_reflection_lounge.png"
}];
function TimelineStep({
  step,
  index,
  "data-index": dataIndex
}: {
  step: (typeof experienceSteps)[0];
  index: number;
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="145867" ref={ref as React.RefObject<HTMLDivElement>} data-index={dataIndex} className={`relative flex flex-col md:flex-row items-start md:items-center justify-between w-full mb-24 md:mb-40 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"} ${!isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="absolute left-[24px] md:left-1/2 top-8 md:top-1/2 w-4 h-4 rounded-full bg-chart-2 border-[3px] border-background shadow-sm z-10 -translate-x-1/2 md:-translate-y-1/2 transition-transform duration-700 delay-300" style={{
      transform: isVisible ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0)"
    }} />

      {}
      <div className={`w-full md:w-[42%] pl-16 md:pl-0 pt-6 md:pt-0 ${isEven ? "md:text-right md:pr-12" : "md:text-left md:pl-12"}`}>
        <span className="text-chart-2 font-serif italic tracking-normal text-lg mb-3 block">
          Phase 0{index + 1}
        </span>
        <h3 className="font-sans font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6 leading-tight tracking-tight">
          {step.title}
        </h3>
        <p className="text-foreground/70 text-lg leading-relaxed">
          {step.description}
        </p>
      </div>

      {}
      <div className={`w-full md:w-[48%] pl-16 md:pl-0 mt-10 md:mt-0 relative ${isEven ? "md:pl-12" : "md:pr-12"}`}>
        {}
        <div className="absolute inset-0 bg-chart-2/10 blur-3xl rounded-[24px] -z-10 transform scale-95 translate-y-4" />
        
        <div className="relative overflow-hidden rounded-[24px] shadow-2xl shadow-primary/5 border border-border/60 bg-muted/20 aspect-[4/3]">
          <img src={step.image} alt={step.title} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
        </div>
      </div>
    </div>;
}
export default function TheExperience() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="a-step-by-step-path-to-relief" className="py-24 md:py-32 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[50%] rounded-full bg-chart-4/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-chart-2/5 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className={`text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-sans font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight">
            A Step-by-Step Path to Relief
          </h2>
          <p className="text-lg md:text-xl text-foreground/80 leading-relaxed font-sans">
            Treating you like family through every milestone of your neurological care. From the first conversation to your final recovery check-up, we are by your side.
          </p>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="absolute left-[24px] md:left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-chart-2/30 to-transparent -translate-x-1/2" />

          {}
          <div className="relative z-10">
            {experienceSteps.map((step, index) => <TimelineStep key={index} step={step} index={index} data-index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}